import { lerpColor } from '../../color';
import type { World as Look } from '../../world';
import { calmAt, calmCenter, catenary, glowDot, grain, rgba, spots, vignette, type Painter, type Spot } from '../../art/paint';

const TAU = Math.PI * 2;

interface Tones {
  readonly bg: number;
  readonly far: number;
  readonly ceil: number;
  readonly ink: number;
  readonly rim: number;
  readonly L: number;
  readonly A1: number;
  readonly A2: number;
}

interface Layout {
  readonly portrait: boolean;
  slope(x: number): number;
  readonly church: Spot;
  readonly s: number;
  readonly tuba: Spot;
  readonly tubaR: number;
  readonly boneco: Spot;
  readonly bonecoH: number;
  readonly palm: Spot;
  readonly palmH: number;
  readonly street: number;
  readonly rise: number;
  readonly garlands: readonly (readonly [number, number, number, number, number])[];
  readonly fireworks: readonly (readonly [number, number, number])[];
}

function smoothstep(t: number): number {
  const k = Math.max(0, Math.min(1, t));
  return k * k * (3 - 2 * k);
}

function layoutOf(p: Painter): Layout {
  const { w, h, unit: u } = p;
  const phase = p.rng.float() * TAU;
  if (w / h < 0.8) {
    const s = Math.max(15, u * 0.085);
    const palmH = Math.max(56, u * 0.4);
    const tubaR = Math.max(20, u * 0.12);
    return {
      portrait: true,
      slope: (x) => h * (0.335 - 0.065 * smoothstep(x / w)) + h * 0.004 * Math.sin(x * 0.03 + phase),
      church: { x: w * 0.78, y: h * 0.32 },
      s,
      tuba: { x: Math.max(tubaR * 1.6, w * 0.17), y: h * 0.76 },
      tubaR,
      boneco: { x: w * 0.84, y: h * 0.875 },
      bonecoH: Math.max(70, u * 0.5),
      palm: { x: Math.max(palmH * 0.62, w * 0.12), y: h * 0.335 },
      palmH,
      street: h * 0.885,
      rise: h * 0.015,
      garlands: [[-10, h * 0.085, w + 10, h * 0.085, h * 0.055]],
      fireworks: [[w * 0.22, h * 0.2, Math.max(14, u * 0.09)]],
    };
  }
  const s = Math.max(18, u * 0.075);
  const tubaR = Math.max(24, u * 0.12);
  const palmH = Math.max(70, u * 0.42);
  const top = w * 0.86;
  return {
    portrait: false,
    slope: (x) => h * (0.6 - 0.24 * smoothstep(x / top)) + (x > top ? (x - top) * 0.1 : 0) + h * 0.006 * Math.sin(x * 0.013 + phase),
    church: { x: top, y: h * 0.37 },
    s,
    tuba: { x: Math.max(tubaR * 1.5, w * 0.085), y: h * 0.74 },
    tubaR,
    boneco: { x: w * 0.92, y: h * 0.86 },
    bonecoH: Math.max(90, u * 0.6),
    palm: { x: Math.max(palmH * 0.62, w * 0.15), y: h * 0.58 },
    palmH,
    street: h * 0.885,
    rise: h * 0.04,
    garlands: [
      [-10, h * 0.3, w * 0.36, -6, h * 0.07],
      [w * 0.6, -6, w + 10, h * 0.14, h * 0.05],
    ],
    fireworks: [
      [w * 0.24, h * 0.27, Math.max(18, u * 0.09)],
      [w * 0.965, h * 0.24, Math.max(13, u * 0.065)],
    ],
  };
}

function sky(p: Painter, t: Tones): void {
  const ctx = p.ctx;
  const g = ctx.createLinearGradient(0, 0, 0, p.h);
  g.addColorStop(0, rgba(lerpColor(t.bg, t.ink, 0.35)));
  g.addColorStop(0.45, rgba(t.bg));
  g.addColorStop(1, rgba(lerpColor(t.bg, t.far, 0.7)));
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, p.w, p.h);
}

function stars(p: Painter, lay: Layout, t: Tones): void {
  const ctx = p.ctx;
  for (let i = 0; i < 44; i++) {
    const x = p.rng.float() * p.w;
    const y = p.rng.float() * p.h * 0.5;
    const a = p.rng.range(0.2, 0.55);
    const r = p.rng.range(0.6, 1.4);
    if (y > lay.slope(x) - 14 || calmAt(p, x, y, 0.9)) continue;
    ctx.fillStyle = rgba(t.rim, a);
    ctx.beginPath();
    ctx.arc(x, y, r, 0, TAU);
    ctx.fill();
  }
}

function firework(p: Painter, x: number, y: number, r: number, tone: number): void {
  const ctx = p.ctx;
  glowDot(ctx, x, y, r * 1.3, tone, 0.12);
  ctx.strokeStyle = rgba(tone, 0.42);
  ctx.lineWidth = Math.max(1, r * 0.045);
  ctx.lineCap = 'round';
  ctx.beginPath();
  const rays = 14;
  const spin = p.rng.float() * TAU;
  const ends: Spot[] = [];
  for (let i = 0; i < rays; i++) {
    const a = spin + (i / rays) * TAU + p.rng.range(-0.08, 0.08);
    const k = p.rng.range(0.78, 1);
    ctx.moveTo(x + Math.cos(a) * r * 0.3, y + Math.sin(a) * r * 0.3);
    ctx.lineTo(x + Math.cos(a) * r * k, y + Math.sin(a) * r * k);
    ends.push({ x: x + Math.cos(a) * r * (k + 0.14), y: y + Math.sin(a) * r * (k + 0.14) });
  }
  ctx.stroke();
  ctx.fillStyle = rgba(lerpColor(tone, 0xffffff, 0.2), 0.5);
  for (const e of ends) {
    ctx.beginPath();
    ctx.arc(e.x, e.y, Math.max(0.8, r * 0.035), 0, TAU);
    ctx.fill();
  }
}

function hill(p: Painter, lay: Layout, t: Tones): void {
  const ctx = p.ctx;
  ctx.fillStyle = rgba(t.far);
  ctx.beginPath();
  ctx.moveTo(-10, p.h + 10);
  for (let i = 0; i <= 48; i++) {
    const x = -10 + ((p.w + 20) * i) / 48;
    ctx.lineTo(x, lay.slope(x));
  }
  ctx.lineTo(p.w + 10, p.h + 10);
  ctx.closePath();
  ctx.fill();
  ctx.strokeStyle = rgba(lerpColor(t.far, t.rim, 0.18), 0.35);
  ctx.lineWidth = 1.2;
  ctx.stroke();
}

function arched(ctx: CanvasRenderingContext2D, x: number, y: number, ww: number, wh: number): void {
  ctx.beginPath();
  ctx.moveTo(x - ww / 2, y + wh / 2);
  ctx.lineTo(x - ww / 2, y - wh / 2 + ww / 2);
  ctx.arc(x, y - wh / 2 + ww / 2, ww / 2, Math.PI, 0);
  ctx.lineTo(x + ww / 2, y + wh / 2);
  ctx.closePath();
}

function house(p: Painter, t: Tones, x: number, base: number, hw: number, hh: number, tint: number, lit: boolean, door: boolean): void {
  const ctx = p.ctx;
  const plain = calmAt(p, x, base - hh * 0.5, 0.8);
  const wall = lerpColor(t.far, p.rng.pick([t.A1, t.A2, t.L, t.rim]), plain ? tint * 0.35 : tint);
  const roof = lerpColor(lerpColor(t.far, t.A2, 0.22), t.ink, 0.38);
  const winRoll = [p.rng.float(), p.rng.float(), p.rng.float(), p.rng.float(), p.rng.float(), p.rng.float()];
  ctx.fillStyle = rgba(wall);
  ctx.fillRect(x - hw / 2, base - hh, hw, hh + hh * 0.7);
  ctx.fillStyle = rgba(roof);
  ctx.beginPath();
  ctx.moveTo(x - hw / 2 - 2, base - hh + 1);
  ctx.lineTo(x - hw * 0.36, base - hh - hh * 0.17);
  ctx.lineTo(x + hw * 0.36, base - hh - hh * 0.17);
  ctx.lineTo(x + hw / 2 + 2, base - hh + 1);
  ctx.closePath();
  ctx.fill();
  if (plain) return;
  ctx.fillStyle = rgba(lerpColor(wall, t.rim, 0.16));
  ctx.fillRect(x - hw / 2, base - hh, hw, Math.max(1.5, hh * 0.05));
  const cols = hw > 44 ? 3 : 2;
  const ww = Math.max(3, hw * 0.15);
  const wh = Math.max(5, hh * 0.22);
  let n = 0;
  for (const [row, fy] of [
    [0, 0.68],
    [1, 0.3],
  ] as const) {
    for (let c = 0; c < cols; c++) {
      const wx = x - hw / 2 + (hw * (c + 0.5)) / cols;
      const wy = base - hh * fy;
      const isDoor = door && row === 1 && c === Math.floor(cols / 2);
      const glow = lit && (winRoll[n++ % winRoll.length] ?? 1) < 0.34;
      ctx.fillStyle = glow ? rgba(t.A1, 0.46) : rgba(lerpColor(wall, t.ink, 0.55));
      if (isDoor) arched(ctx, wx, base - hh * 0.2, ww * 1.15, hh * 0.42);
      else arched(ctx, wx, wy, ww, wh);
      ctx.fill();
    }
  }
}

function houses(p: Painter, lay: Layout, t: Tones): void {
  const u = p.unit;
  const lit = p.level >= 2;
  const rows = lay.portrait ? [2, 1, 0] : [0, 1];
  for (const row of rows) {
    const scale = lay.portrait ? 1 - row * 0.12 : row === 0 ? 0.9 : 1;
    const tint = lay.portrait ? 0.2 - row * 0.04 : row === 0 ? 0.14 : 0.2;
    let x = -p.rng.range(0, 0.06) * u - (row % 2) * 0.05 * u;
    while (x < p.w + 0.1 * u) {
      const hw = Math.max(14, p.rng.range(0.1, 0.15) * u * scale);
      const hh = hw * p.rng.range(0.85, 1.2);
      const cx = x + hw / 2;
      const lift = lay.portrait ? row * Math.max(12, 0.09 * u) : row === 0 ? 0 : -0.13 * u;
      const base = lay.slope(cx) + hh * 0.25 - lift;
      house(p, t, cx, base, hw, hh, tint, lit, row === 0 || !lay.portrait);
      x += hw + p.rng.range(-1, 2);
    }
  }
}

function church(p: Painter, lay: Layout, t: Tones): void {
  const ctx = p.ctx;
  const { x, y } = lay.church;
  const s = lay.s;
  const lit = p.level >= 2;
  const line = lerpColor(t.bg, t.ink, 0.5);
  const tower = lerpColor(t.ceil, t.far, 0.25);
  const dark = lerpColor(t.far, t.ink, 0.45);
  glowDot(ctx, x, y - 1.8 * s, 3.6 * s, t.A1, 0.09);
  ctx.lineWidth = Math.max(1, s * 0.06);
  ctx.strokeStyle = rgba(line, 0.55);
  for (const tx of [x - 1.35 * s, x + 0.75 * s]) {
    ctx.fillStyle = rgba(tower);
    ctx.fillRect(tx, y - 3 * s, 0.6 * s, 3 * s + 6);
    ctx.strokeRect(tx, y - 3 * s, 0.6 * s, 3 * s + 6);
    ctx.beginPath();
    ctx.moveTo(tx - 0.05 * s, y - 3 * s);
    ctx.lineTo(tx + 0.3 * s, y - 3.55 * s);
    ctx.lineTo(tx + 0.65 * s, y - 3 * s);
    ctx.closePath();
    ctx.fillStyle = rgba(lerpColor(tower, t.ink, 0.3));
    ctx.fill();
    ctx.stroke();
    ctx.fillStyle = rgba(dark);
    arched(ctx, tx + 0.3 * s, y - 2.45 * s, 0.26 * s, 0.5 * s);
    ctx.fill();
    ctx.beginPath();
    ctx.moveTo(tx + 0.3 * s, y - 3.55 * s);
    ctx.lineTo(tx + 0.3 * s, y - 3.85 * s);
    ctx.moveTo(tx + 0.19 * s, y - 3.75 * s);
    ctx.lineTo(tx + 0.41 * s, y - 3.75 * s);
    ctx.stroke();
  }
  ctx.fillStyle = rgba(t.ceil);
  ctx.beginPath();
  ctx.moveTo(x - 0.75 * s, y + 6);
  ctx.lineTo(x - 0.75 * s, y - 2.3 * s);
  ctx.quadraticCurveTo(x - 0.42 * s, y - 2.32 * s, x - 0.32 * s, y - 2.62 * s);
  ctx.lineTo(x, y - 2.92 * s);
  ctx.lineTo(x + 0.32 * s, y - 2.62 * s);
  ctx.quadraticCurveTo(x + 0.42 * s, y - 2.32 * s, x + 0.75 * s, y - 2.3 * s);
  ctx.lineTo(x + 0.75 * s, y + 6);
  ctx.closePath();
  ctx.fill();
  ctx.stroke();
  ctx.beginPath();
  ctx.moveTo(x, y - 2.92 * s);
  ctx.lineTo(x, y - 3.25 * s);
  ctx.moveTo(x - 0.12 * s, y - 3.14 * s);
  ctx.lineTo(x + 0.12 * s, y - 3.14 * s);
  ctx.stroke();
  ctx.strokeStyle = rgba(lerpColor(t.ceil, t.rim, 0.2), 0.4);
  ctx.beginPath();
  ctx.moveTo(x - 0.75 * s, y - 2.05 * s);
  ctx.lineTo(x + 0.75 * s, y - 2.05 * s);
  ctx.stroke();
  ctx.fillStyle = lit ? rgba(t.A1, 0.42) : rgba(dark);
  ctx.beginPath();
  ctx.arc(x, y - 1.6 * s, 0.17 * s, 0, TAU);
  ctx.fill();
  ctx.fillStyle = lit ? rgba(lerpColor(dark, t.A1, 0.35)) : rgba(dark);
  arched(ctx, x, y - 0.35 * s, 0.46 * s, 0.75 * s);
  ctx.fill();
  ctx.fillStyle = rgba(dark);
  for (const dx of [-0.45, 0.45]) {
    arched(ctx, x + dx * s, y - 1.0 * s, 0.18 * s, 0.36 * s);
    ctx.fill();
  }
}

function palm(p: Painter, lay: Layout, t: Tones): void {
  const ctx = p.ctx;
  const { x, y } = lay.palm;
  const H = lay.palmH;
  const lean = lay.portrait ? 0.12 : -0.16;
  const topX = x + lean * H;
  const topY = y - H;
  const tone = lerpColor(t.far, t.ink, 0.42);
  ctx.strokeStyle = rgba(tone);
  ctx.lineCap = 'round';
  for (let i = 0; i < 6; i++) {
    const k0 = i / 6;
    const k1 = (i + 1) / 6;
    ctx.lineWidth = Math.max(2, H * (0.07 - 0.035 * k0));
    ctx.beginPath();
    ctx.moveTo(x + lean * H * k0 * k0, y - H * k0);
    ctx.lineTo(x + lean * H * k1 * k1, y - H * k1);
    ctx.stroke();
  }
  ctx.fillStyle = rgba(tone);
  for (let i = 0; i < 7; i++) {
    const a = -Math.PI / 2 + (i - 3) * 0.5 + p.rng.range(-0.08, 0.08);
    const len = H * p.rng.range(0.42, 0.55);
    const tipX = topX + Math.cos(a) * len;
    const tipY = topY + Math.sin(a) * len * 0.75 + len * 0.28;
    ctx.beginPath();
    ctx.moveTo(topX, topY);
    ctx.quadraticCurveTo(topX + Math.cos(a - 0.35) * len * 0.7, topY + Math.sin(a - 0.35) * len * 0.7 - len * 0.12, tipX, tipY);
    ctx.quadraticCurveTo(topX + Math.cos(a + 0.2) * len * 0.5, topY + Math.sin(a + 0.2) * len * 0.5, topX, topY);
    ctx.fill();
  }
  ctx.fillStyle = rgba(lerpColor(t.far, t.A1, 0.18));
  for (const [dx, dy] of [
    [-0.03, 0.03],
    [0.025, 0.035],
    [0, 0.06],
  ] as const) {
    ctx.beginPath();
    ctx.arc(topX + dx * H, topY + dy * H, Math.max(1.5, H * 0.025), 0, TAU);
    ctx.fill();
  }
}

function umbrella(p: Painter, x: number, y: number, s: number, tone: number, other: number, t: Tones): void {
  const ctx = p.ctx;
  ctx.save();
  ctx.translate(x, y);
  ctx.rotate(p.rng.range(-0.22, 0.22));
  ctx.strokeStyle = rgba(lerpColor(t.far, t.rim, 0.3), 0.5);
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(0, 0);
  ctx.lineTo(0, s * 0.35);
  ctx.moveTo(0, s * 0.95);
  ctx.lineTo(0, s * 1.75);
  ctx.arc(-s * 0.16, s * 1.75, s * 0.16, 0, Math.PI);
  ctx.stroke();
  for (let i = 0; i < 4; i++) {
    ctx.fillStyle = rgba(i % 2 === 0 ? tone : other, 0.55);
    ctx.beginPath();
    ctx.moveTo(0, s * 1.0);
    ctx.arc(0, s * 1.0, s * 0.95, Math.PI + (i * Math.PI) / 4, Math.PI + ((i + 1) * Math.PI) / 4);
    ctx.closePath();
    ctx.fill();
  }
  ctx.strokeStyle = rgba(t.ink, 0.45);
  ctx.beginPath();
  ctx.arc(0, s * 1.0, s * 0.95, Math.PI, 0);
  ctx.closePath();
  ctx.stroke();
  ctx.restore();
}

function garland(p: Painter, x0: number, y0: number, x1: number, y1: number, sagBy: number, t: Tones, offset: number): void {
  const ctx = p.ctx;
  ctx.strokeStyle = rgba(lerpColor(t.far, t.rim, 0.28), 0.6);
  ctx.lineWidth = 1.2;
  ctx.beginPath();
  for (let i = 0; i <= 30; i++) {
    const at = catenary(x0, y0, x1, y1, sagBy, i / 30);
    if (i === 0) ctx.moveTo(at.x, at.y);
    else ctx.lineTo(at.x, at.y);
  }
  ctx.stroke();
  const s = Math.max(7, p.unit * 0.032);
  const span = Math.hypot(x1 - x0, y1 - y0);
  const count = Math.max(3, Math.floor(span / (s * 3.1)));
  for (let i = 1; i < count; i++) {
    const at = catenary(x0, y0, x1, y1, sagBy, i / count);
    if (at.y < s * 0.5 || calmAt(p, at.x, at.y + s, 0.95)) continue;
    const first = (i + offset) % 2 === 0;
    umbrella(p, at.x, at.y, s, first ? t.A1 : t.A2, first ? t.A2 : t.A1, t);
  }
}

function street(p: Painter, lay: Layout, t: Tones): void {
  const ctx = p.ctx;
  const top = (x: number): number => lay.street - (lay.rise * x) / p.w;
  const road = lerpColor(t.bg, t.ink, 0.3);
  ctx.fillStyle = rgba(road);
  ctx.beginPath();
  ctx.moveTo(-10, p.h + 10);
  ctx.lineTo(-10, top(-10));
  ctx.lineTo(p.w + 10, top(p.w + 10));
  ctx.lineTo(p.w + 10, p.h + 10);
  ctx.closePath();
  ctx.fill();
  ctx.strokeStyle = rgba(lerpColor(t.far, t.rim, 0.2), 0.35);
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  ctx.moveTo(-10, top(-10));
  ctx.lineTo(p.w + 10, top(p.w + 10));
  ctx.stroke();
  const sw = Math.max(14, p.unit * 0.06);
  const sh = Math.max(9, sw * 0.55);
  const stones: number[] = [];
  for (let row = 0; top(0) + (row + 0.5) * sh < p.h + sh; row++) {
    let x = -sw * (row % 2 === 0 ? 0.2 : 0.7);
    while (x < p.w + sw) {
      const ww = sw * p.rng.range(0.7, 1.15);
      const cx = x + ww / 2;
      const cy = top(cx) + (row + 0.6) * sh + p.rng.range(-1.5, 1.5);
      stones.push(cx, cy, ww * 0.44, sh * p.rng.range(0.32, 0.42), p.rng.range(-0.15, 0.15));
      x += ww + p.rng.range(1, 3);
    }
  }
  ctx.fillStyle = rgba(t.far, 0.55);
  ctx.beginPath();
  for (let i = 0; i < stones.length; i += 5) {
    const [x, y, rx, ry, rot] = [stones[i]!, stones[i + 1]!, stones[i + 2]!, stones[i + 3]!, stones[i + 4]!];
    ctx.moveTo(x + rx, y);
    ctx.ellipse(x, y, rx, ry, rot, 0, TAU);
  }
  ctx.fill();
  ctx.strokeStyle = rgba(lerpColor(t.far, t.rim, 0.12), 0.2);
  ctx.lineWidth = 1;
  ctx.beginPath();
  for (let i = 0; i < stones.length; i += 5) {
    const [x, y, rx, ry, rot] = [stones[i]!, stones[i + 1]!, stones[i + 2]!, stones[i + 3]!, stones[i + 4]!];
    ctx.moveTo(x - rx * 0.7, y - ry * 0.5);
    ctx.ellipse(x, y, rx * 0.86, ry * 0.8, rot, Math.PI * 1.1, Math.PI * 1.9);
  }
  ctx.stroke();
}

function tuba(p: Painter, lay: Layout, t: Tones): void {
  const ctx = p.ctx;
  const { x, y } = lay.tuba;
  const r = lay.tubaR;
  const metal = lerpColor(t.far, t.A1, 0.2);
  const shade = lerpColor(metal, t.ink, 0.45);
  const shine = lerpColor(t.far, t.A1, 0.55);
  const lx = x + r * 0.35;
  const ly = y + r * 1.5;
  const lr = r * 0.7;
  ctx.lineCap = 'round';
  ctx.beginPath();
  ctx.arc(lx, ly, lr, -Math.PI * 0.62, Math.PI * 1.25);
  ctx.strokeStyle = rgba(shade);
  ctx.lineWidth = r * 0.3;
  ctx.stroke();
  ctx.strokeStyle = rgba(metal);
  ctx.lineWidth = r * 0.19;
  ctx.stroke();
  ctx.strokeStyle = rgba(shine, 0.45);
  ctx.lineWidth = Math.max(1, r * 0.045);
  ctx.beginPath();
  ctx.arc(lx, ly, lr - r * 0.03, Math.PI * 0.9, Math.PI * 1.2);
  ctx.stroke();
  for (let i = 0; i < 3; i++) {
    const vx = lx + lr * 0.35 + i * r * 0.17;
    const vy = ly - lr * 0.95;
    ctx.fillStyle = rgba(shade);
    ctx.fillRect(vx - r * 0.055, vy - r * 0.38, r * 0.11, r * 0.36);
    ctx.fillStyle = rgba(shine, 0.6);
    ctx.beginPath();
    ctx.arc(vx, vy - r * 0.4, r * 0.075, 0, TAU);
    ctx.fill();
  }
  ctx.beginPath();
  ctx.ellipse(x, y, r, r * 0.92, -0.12, 0, TAU);
  ctx.fillStyle = rgba(metal);
  ctx.fill();
  ctx.strokeStyle = rgba(shine, 0.7);
  ctx.lineWidth = Math.max(1.5, r * 0.08);
  ctx.stroke();
  const mouth = ctx.createRadialGradient(x + r * 0.1, y + r * 0.1, r * 0.05, x + r * 0.05, y + r * 0.04, r * 0.66);
  mouth.addColorStop(0, rgba(t.ink, 0.9));
  mouth.addColorStop(1, rgba(shade, 0.85));
  ctx.fillStyle = mouth;
  ctx.beginPath();
  ctx.ellipse(x + r * 0.05, y + r * 0.04, r * 0.66, r * 0.6, -0.12, 0, TAU);
  ctx.fill();
  ctx.strokeStyle = rgba(lerpColor(shine, t.rim, 0.4), 0.4);
  ctx.lineWidth = Math.max(1, r * 0.05);
  ctx.beginPath();
  ctx.ellipse(x, y, r * 0.86, r * 0.79, -0.12, Math.PI * 1.1, Math.PI * 1.45);
  ctx.stroke();
}

function confetti(p: Painter, t: Tones): void {
  const ctx = p.ctx;
  for (const at of spots(p, 34, 1.05, 12, 22)) {
    const tone = lerpColor(t.bg, p.rng.pick([t.A1, t.A2, t.L]), 0.5);
    ctx.save();
    ctx.translate(at.x, at.y);
    ctx.rotate(p.rng.float() * TAU);
    ctx.fillStyle = rgba(tone, 0.75);
    if (p.rng.chance(0.4)) {
      ctx.beginPath();
      ctx.arc(0, 0, 2.6, 0, TAU);
      ctx.fill();
    } else ctx.fillRect(-4, -2, 8, 4);
    ctx.restore();
  }
}

function boneco(p: Painter, lay: Layout, t: Tones): void {
  const ctx = p.ctx;
  const { x, y } = lay.boneco;
  const H = lay.bonecoH;
  const line = lerpColor(t.bg, t.ink, 0.5);
  const robe = lerpColor(t.far, t.A2, 0.17);
  const sleeve = lerpColor(t.far, t.A2, 0.1);
  const skin = lerpColor(t.far, t.rim, 0.14);
  ctx.strokeStyle = rgba(line, 0.5);
  ctx.lineWidth = Math.max(1, H * 0.012);
  ctx.lineCap = 'round';
  for (const side of [-1, 1]) {
    ctx.fillStyle = rgba(sleeve);
    ctx.beginPath();
    ctx.moveTo(x + side * H * 0.13, y - H * 0.7);
    ctx.quadraticCurveTo(x + side * H * 0.27, y - H * 0.5, x + side * H * 0.26, y - H * 0.3);
    ctx.lineTo(x + side * H * 0.2, y - H * 0.3);
    ctx.quadraticCurveTo(x + side * H * 0.19, y - H * 0.5, x + side * H * 0.08, y - H * 0.62);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    ctx.fillStyle = rgba(skin);
    ctx.beginPath();
    ctx.arc(x + side * H * 0.23, y - H * 0.27, H * 0.035, 0, TAU);
    ctx.fill();
  }
  ctx.fillStyle = rgba(robe);
  ctx.beginPath();
  ctx.moveTo(x - H * 0.14, y - H * 0.7);
  ctx.lineTo(x + H * 0.14, y - H * 0.7);
  ctx.lineTo(x + H * 0.22, y + 4);
  ctx.lineTo(x - H * 0.22, y + 4);
  ctx.closePath();
  ctx.fill();
  ctx.stroke();
  ctx.fillStyle = rgba(lerpColor(t.far, t.A1, 0.24));
  ctx.beginPath();
  ctx.ellipse(x, y - H * 0.7, H * 0.17, H * 0.045, 0, 0, TAU);
  ctx.fill();
  ctx.fillStyle = rgba(skin);
  ctx.beginPath();
  ctx.arc(x, y - H * 0.85, H * 0.13, 0, TAU);
  ctx.fill();
  ctx.stroke();
  ctx.fillStyle = rgba(lerpColor(t.far, t.ink, 0.35));
  ctx.beginPath();
  ctx.ellipse(x, y - H * 0.94, H * 0.2, H * 0.035, 0, 0, TAU);
  ctx.fill();
  ctx.beginPath();
  ctx.arc(x, y - H * 0.95, H * 0.1, Math.PI, 0);
  ctx.fill();
  ctx.fillStyle = rgba(t.ink, 0.55);
  for (const dx of [-0.045, 0.045]) {
    ctx.beginPath();
    ctx.arc(x + dx * H, y - H * 0.86, Math.max(1, H * 0.014), 0, TAU);
    ctx.fill();
  }
  ctx.strokeStyle = rgba(t.ink, 0.45);
  ctx.beginPath();
  ctx.arc(x, y - H * 0.83, H * 0.05, 0.2 * Math.PI, 0.8 * Math.PI);
  ctx.stroke();
}

export function paint(p: Painter, look: Look): void {
  const pal = look.palette;
  const t: Tones = {
    bg: pal.bg,
    far: lerpColor(pal.bg, look.ceil, 0.28),
    ceil: look.ceil,
    ink: pal.ink,
    rim: pal.rim,
    L: pal.incoming.circle,
    A1: pal.held,
    A2: pal.spot,
  };
  const lay = layoutOf(p);
  sky(p, t);
  stars(p, lay, t);
  if (p.level >= 4) {
    lay.fireworks.forEach(([x, y, r], i) => {
      if (!calmAt(p, x, y, 0.8)) firework(p, x, y, r, i % 2 === 0 ? t.A1 : t.A2);
    });
  }
  hill(p, lay, t);
  houses(p, lay, t);
  if (p.level >= 3) palm(p, lay, t);
  church(p, lay, t);
  const shown = lay.portrait || p.level < 2 ? lay.garlands.slice(0, 1) : lay.garlands;
  shown.forEach(([x0, y0, x1, y1, sagBy], i) => {
    garland(p, x0, y0, x1, y1, sagBy, t, i);
  });
  street(p, lay, t);
  if (p.level >= 5) confetti(p, t);
  tuba(p, lay, t);
  if (p.level >= 6) boneco(p, lay, t);
  calmCenter(p, t.bg, 0.62, 1.15);
  grain(p, 0.05, true);
  vignette(p, t.ink, 0.45);
}
